import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import TestimonialService from "../../../services/KangarooServices/TestimonialService";
import StudentService from "../../../services/KangarooServices/StudentService";
import { Card, CardBody, Col, Container, Input, Label, Row } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import DestinationService from "../../../services/KangarooServices/DestinationService";
import UniversityService from "../../../services/KangarooServices/UniversityService";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import MediaService from "../../../services/KangarooServices/MediaServices";
import MediaModal from "../../PagesSection/Component/MediaModal";
import { ClockFading } from "lucide-react";
import { imageBaseApi } from "../../../utilis/imageHelper";

const TestimonialForm = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [initialdata, setInitialData] = useState({});

  const [mediaData, setMediaData] = useState([]);
  const [destinationdata, setDestinationData] = useState([]);
  const [universitydata, setUniversityData] = useState([]);

  const toggleMedia = () => setIsMediaOpen(!isMediaOpen);

  const fetchMedia = async (page = 1, size = 1000) => {
    try {
      const response = await MediaService.getList({ page, pageSize: size });
      console.log("Fetched Media:", response);
      setMediaData(response);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch dropdown data
  useEffect(() => {
    DestinationService.getList().then((res) => setDestinationData(res));
    UniversityService.getList().then((res) => setUniversityData(res));
  }, []);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const response = await TestimonialService.getDetail(id);
      console.log("API Response from getDetail:", response);

      // Set initial data
      setInitialData(response);

      // Set selected media if studentPhoto exists
      if (response?.studentPhoto) {
        // Infer mimeType from fileName if not present
        const media = { ...response.studentPhoto };
        if (!media.mimeType && media.fileName) {
          const ext = media.fileName.split(".").pop().toLowerCase();
          const mimeTypes = {
            png: "image/png",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            gif: "image/gif",
            webp: "image/webp",
            svg: "image/svg+xml",
            pdf: "application/pdf",
          };
          media.mimeType = mimeTypes[ext] || "image/png"; // default to image
        }
        setSelectedMedia(media);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
  }, [id]);

  // Set selected media after both mediaData and initialdata are loaded
  useEffect(() => {
    if (initialdata?.studentPhoto && !selectedMedia) {
      setSelectedMedia(initialdata.studentPhoto);
    }
  }, [initialdata?.studentPhoto]);

  // Dropdown mapped data
  const mediaOptions = mediaData?.items?.map((i) => ({
    value: i.id,
    label: i.fileName,
  }));

  const destinationOptions = destinationdata?.map((i) => ({
    value: i.id,
    label: i.name,
  }));

  const universityOptions = universitydata?.map((i) => ({
    value: i.id,
    label: i.name,
  }));

  // Validation Schema
  const validationSchema = Yup.object({
    studentName: Yup.string().required("Student name is required"),
    courseName: Yup.string().required("Course name is required"),
    text: Yup.string().required("Text is required"),
    rating: Yup.number()
      .nullable()
      .min(1, "Rating must be 1–5")
      .max(5, "Rating must be 1–5"),
    videoUrl: Yup.string().url("Invalid URL").nullable(),
  });

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      studentName: initialdata?.studentName || "",
      studentPhotoId: initialdata?.studentPhoto?.id || null,
      destinationCountryId: initialdata?.destinationCountryId || null,
      universityId: initialdata?.universityId || null,
      courseName: initialdata?.courseName || "",
      text: initialdata?.text || "",
      rating: initialdata?.rating || "",
      videoUrl: initialdata?.videoUrl || "",
      isFeatured: initialdata?.isFeatured || false,
      orderIndex: initialdata?.orderIndex,
    },

    validationSchema,

    onSubmit: async (values) => {
      console.log(values);
      try {
        setLoading(true);

        const payload = {
          id: id || null,
          studentName: values.studentName,
          studentPhotoId: selectedMedia?.id || null,
          destinationCountryId: values.destinationCountryId || null,
          universityId: values.universityId || null,
          courseName: values.courseName,
          text: values.text,
          rating: values.rating ? Number(values.rating) : null,
          videoUrl: values.videoUrl || null,
          isFeatured: Boolean(values.isFeatured),
          orderIndex: values?.orderIndex,
        };

        if (id) {
          await TestimonialService.update(id, payload);
          toast.success("Testimonials updated successfully");
        } else {
          await TestimonialService.create(payload);
          toast.success("Testimonial Added Successfully");
        }
        navigate("/testimonial");
      } catch (error) {
        console.log("API ERROR:", error.response?.data || error);
        toast.error(
          error.response?.data?.message || "Failed to create testimonial"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  console.log("Initial", formik.values.destinationCountryId);
  const destination = destinationOptions?.find(
    (x) => x.value === formik.values.destinationCountryId
  );
  console.log("Destination", destinationOptions);
  const breadcrumbItems = [{ title: "Back to List", link: "/testimonial" }];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Testimonial Form"
          pageTitle="Testimonial"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button
              type="submit"
              className="btn btn-success"
              disabled={loading}
            >
              {id ? "Update" : "Create"}
              {/* {loading ? "Submitting..." : "Submit"} */}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/testimonial")}
              disabled={loading}
            >
              Cancel
            </button>
          </div>

          <Row>
            <Col lg={8}>
              <Card>
                <CardBody>
                  {/* Student Name */}
                  <div className="mb-3">
                    <Label>
                      Student Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="studentName"
                      value={formik.values.studentName}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.studentName &&
                      formik.errors.studentName && (
                        <div className="text-danger mt-1">
                          {formik.errors.studentName}
                        </div>
                      )}
                  </div>

                  {/* Course Name */}
                  <div className="mb-3">
                    <Label>
                      Course Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="courseName"
                      value={formik.values.courseName}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.courseName && formik.errors.courseName && (
                      <div className="text-danger mt-1">
                        {formik.errors.courseName}
                      </div>
                    )}
                  </div>

                  {/* Video URL */}
                  <div className="mb-3">
                    <Label>Video URL</Label>
                    <Input
                      name="videoUrl"
                      value={formik.values.videoUrl}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.videoUrl && formik.errors.videoUrl && (
                      <div className="text-danger mt-1">
                        {formik.errors.videoUrl}
                      </div>
                    )}
                  </div>

                  {/* Text */}
                  <div className="mb-3">
                    <Label>
                      Text <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      name="text"
                      rows="5"
                      value={formik.values.text}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.text && formik.errors.text && (
                      <div className="text-danger mt-1">
                        {formik.errors.text}
                      </div>
                    )}
                  </div>

                  {/* Featured */}
                  <div className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        name="isFeatured"
                        checked={formik.values.isFeatured}
                        onChange={(e) =>
                          formik.setFieldValue("isFeatured", e.target.checked)
                        }
                      />{" "}
                      <strong>Mark as Featured</strong>
                    </Label>
                  </div>

                  {/* Rating */}
                  <div className="mb-3">
                    <Label>Rating (1–5)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      name="rating"
                      value={formik.values.rating}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.rating && formik.errors.rating && (
                      <div className="text-danger mt-1">
                        {formik.errors.rating}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col>
              <Card>
                <CardBody>
                  {/* Student Photo */}
                  <div className="d-flex justify-content-between align-items-center">
                    <Label style={{ cursor: "pointer" }}>Media</Label>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={toggleMedia}
                    >
                      +
                    </button>
                  </div>

                  {selectedMedia ? (
                    <div className="mt-2 text-center">
                      {selectedMedia.mimeType?.startsWith("image") && (
                        <img
                          src={`${imageBaseApi}${selectedMedia?.url}`}
                          alt={selectedMedia.fileName}
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            borderRadius: "6px",
                          }}
                        />
                      )}
                      {selectedMedia.mimeType === "application/pdf" && (
                        <a
                          href={selectedMedia.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-block mt-2"
                        >
                          📄 {selectedMedia.fileName}
                        </a>
                      )}
                      {!selectedMedia.mimeType?.startsWith("image") &&
                        selectedMedia.mimeType !== "application/pdf" && (
                          <span className="d-block mt-2">
                            ❓ {selectedMedia.fileName}
                          </span>
                        )}
                    </div>
                  ) : (
                    <p className="text-muted mt-2">No media selected</p>
                  )}
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <div className="mb-3">
                    <Label>Order Index</Label>
                    <Input
                      name="orderIndex"
                      value={formik.values.orderIndex}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.orderIndex && formik.errors.orderIndex && (
                      <div className="text-danger mt-1">
                        {formik.errors.orderIndex}
                      </div>
                    )}
                  </div>
                  {/* Destination Country */}
                  <div className="mb-3">
                    <Label>Destination Country</Label>
                    <Select
                      name="destinationCountryId"
                      options={destinationOptions}
                      value={destinationOptions?.find(
                        (x) => x.value === formik.values.destinationCountryId
                      )}
                      onChange={(option) =>
                        formik.setFieldValue(
                          "destinationCountryId",
                          option?.value || null
                        )
                      }
                      isClearable
                    />
                  </div>

                  {/* University */}
                  <div className="mb-3">
                    <Label>University</Label>
                    <Select
                      name="universityId"
                      options={universityOptions}
                      value={universityOptions?.find(
                        (x) => x.value === formik.values.universityId
                      )}
                      onChange={(option) =>
                        formik.setFieldValue(
                          "universityId",
                          option?.value || null
                        )
                      }
                      isClearable
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
        <MediaModal
          isOpen={isMediaOpen}
          toggle={toggleMedia}
          mediaData={mediaData}
          selectedMedia={selectedMedia}
          setSelectedMedia={setSelectedMedia}
        />
      </div>
    </Container>
  );
};

export default TestimonialForm;
